import React, { useEffect } from "react";
import { Box, Grid, Typography, CircularProgress, Alert } from "@mui/material";
import {
  useGetInvoiceQuery,
  useGetTransactionLadgerQuery,
} from "../../api/api-invoices";
import { useGetCurrentUserQuery } from "../../api/api-users-v2";

import Performance from "../components/Performance";
import ImporterExposure from "../components/ImporterExposure";
import ActiveInvoices from "../components/ActiveInvoices";
import InvestorStats from "../components/InvestorStats";
import PortfolioValuations from "../components/PortfolioValuation";

const InvestorDashboard: React.FC = () => {
  const {
    data: currentUser,
    isLoading: isUserLoading,
    error: userError,
  } = useGetCurrentUserQuery();

  const id = currentUser?.id;

  const {
    data: invoiceData,
    isLoading,
    isError,
  } = useGetInvoiceQuery(
    {
      endpoint: `dashboard/investors-summary`,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id, // Skip if id is not available
    }
  );

  const {
    data: transactionData,
    isLoading: transactionsLoading,
    error: transactionError,
  } = useGetTransactionLadgerQuery(
    { transparams: `?userId=${id}` },
    { skip: isUserLoading || !id } // Skip this query until user data is loaded and id is available
  );

  useEffect(() => {}, [invoiceData, transactionData]);

  // Extract funder_name for the current investor (if available)
  let funderName = "";
  if (
    invoiceData &&
    Array.isArray(invoiceData.funders) &&
    invoiceData.funders.length > 0
  ) {
    // If only one funder, use that; otherwise, try to match by user id if possible
    if (invoiceData.funders.length === 1) {
      funderName = invoiceData.funders[0].funder_name;
    } else if (currentUser && currentUser.id) {
      // Try to match by id (if available)
      const match = invoiceData.funders.find(
        (f: any) => f.funder_id === currentUser.id
      );
      funderName = match
        ? match.funder_name
        : invoiceData.funders[0].funder_name;
    } else {
      funderName = invoiceData.funders[0].funder_name;
    }
  }

  // Show loading state while fetching user data
  if (isUserLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading user data...
        </Typography>
      </Box>
    );
  }

  if (userError) {
    return (
      <Alert severity="error">
        Error loading user data. Please refresh and try again.
      </Alert>
    );
  }

  if (!id) {
    return (
      <Alert severity="warning">
        No user ID found for current user. Please contact support.
      </Alert>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        Error loading invoice data:{" "}
        {(isError as any)?.message || "Unknown error"}.
      </Alert>
    );
  }

  if (transactionError) {
    return (
      <Alert severity="error">
        Error loading transaction data:{" "}
        {(transactionError as any)?.message || "Unknown error"}.
      </Alert>
    );
  }

  return (
    <Box className="p-8 bg-gray-100 min-h-screen">
      {funderName && (
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
          Welcome
          <Box
            component="span"
            sx={(theme) => ({
              fontWeight: 700,
              ml: 1,
              px: 2,
              py: 0.5,
              borderRadius: 2,
              boxShadow: 2,
              display: "inline-block",
              letterSpacing: 1,
              fontSize: "1.2em",
            })}
          >
            {funderName}
          </Box>
        </Typography>
      )}
      <Grid
        container
        spacing={2}
        mb={2}
        sx={{ display: "flex", alignItems: "stretch" }}
      >
        <Grid
          item
          xs={12}
          md={4}
          lg={4}
          sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
        >
          <PortfolioValuations data={invoiceData} />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          lg={4}
          sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
        >
          <Performance data={invoiceData} />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          lg={4}
          sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
        >
          <InvestorStats data={invoiceData} />
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        mb={2}
        sx={{ display: "flex", alignItems: "stretch" }}
      >
        <Grid
          item
          xs={12}
          md={6}
          lg={6}
          sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
        >
          <ImporterExposure data={invoiceData} tabIndex="importer" />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={6}
          sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
        >
          <ImporterExposure data={invoiceData} tabIndex="exporter" />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={6}
          sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
        >
          <ImporterExposure data={invoiceData} tabIndex="industry" />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={6}
          sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
        >
          <ImporterExposure data={invoiceData} tabIndex="exporter_countries" />
        </Grid>
      </Grid>

      <Grid container spacing={1} mb={2}>
        {" "}
        {/* reduced spacing and mb */}
        <Grid item xs={12} md={8} lg={12}>
          <ActiveInvoices />
        </Grid>
      </Grid>
    </Box>
  );
};

export default InvestorDashboard;
